/**
 * Season utilities — single source of truth for season labels site-wide.
 *
 * The English football season runs Aug → May, but registration windows,
 * pre-season friendlies, and fixture releases all happen from ~July, so
 * we roll over on **July 1** each year. From that date, the "current"
 * selectable season becomes the upcoming league year.
 *
 *   Example: on 2026-07-01, currentSeason() returns "2026-27".
 *            on 2026-06-30, currentSeason() returns "2025-26".
 *
 * The rollover is automatic and works forever — no annual maintenance.
 */

const ROLLOVER_MONTH = 7; // July = 7. Change here if the football calendar shifts.

/**
 * Return the season label ("YYYY-YY") for a given Date.
 * Defaults to now. Rolls over on 1 July every year.
 */
export function currentSeasonFor(date = new Date()) {
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth() + 1; // 1-based
  const start = m >= ROLLOVER_MONTH ? y : y - 1;
  const endShort = String((start + 1) % 100).padStart(2, "0");
  return `${start}-${endShort}`;
}

/**
 * Shorthand: the current active season.
 */
export function currentSeason() {
  return currentSeasonFor();
}

/**
 * Return a list of `count` seasons ending at (and including) the current
 * season. Newest first — e.g. on 2026-08 with count=5 →
 * ["2026-27", "2025-26", "2024-25", "2023-24", "2022-23"].
 */
export function recentSeasons(count = 5) {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth() + 1;
  const currentStart = m >= ROLLOVER_MONTH ? y : y - 1;
  const seasons = [];
  for (let i = 0; i < count; i += 1) {
    const s = currentStart - i;
    const endShort = String((s + 1) % 100).padStart(2, "0");
    seasons.push(`${s}-${endShort}`);
  }
  return seasons;
}

/**
 * True when the given season label is the currently active season.
 */
export function isCurrentSeason(seasonLabel) {
  return seasonLabel === currentSeason();
}
